<script lang="ts">
	import { ChevronLeft, ChevronRight } from '@lucide/svelte';
	import { cn } from '$lib/utils';
	import type { TestimonialData } from './landing-types';

	let {
		data,
		class: className
	}: {
		data: TestimonialData;
		class?: string;
	} = $props();

	let currentIndex = $state(0);

	function nextTestimonial(totalCount: number) {
		if (totalCount > 0) {
			currentIndex = (currentIndex + 1) % totalCount;
		}
	}

	function previousTestimonial(totalCount: number) {
		if (totalCount > 0) {
			currentIndex = (currentIndex - 1 + totalCount) % totalCount;
		}
	}

	function goToTestimonial(index: number) {
		currentIndex = index;
	}
</script>

<section class={cn('relative h-fit md:mx-8 py-16 md:py-24 lg:py-32 my-8', className)}>
	<!-- Loaded State -->
	{#if data.testimonials.length > 0}
		{@const safeIndex = currentIndex >= data.testimonials.length ? 0 : currentIndex}
		{@const currentTestimonial = data.testimonials[safeIndex]}
		{#if currentTestimonial}
			<!-- Background Image with Overlay -->
			<div class="absolute inset-0 rounded-2xl">
				<img
					src={currentTestimonial.backgroundImageUrl}
					alt="Background"
					class="w-full h-full object-cover rounded-2xl"
				/>
				<div class="absolute inset-0 bg-black/70 rounded-2xl"></div>
			</div>

			<!-- Content -->
			<div class="relative mx-auto px-4 md:px-6 lg:px-8 max-w-5xl rounded-2xl">
				<div class="flex flex-col items-center justify-center text-center space-y-8">
					<!-- Quote -->
					<blockquote class="text-white">
						<p
							class="font-title text-2xl md:text-3xl lg:text-4xl font-bold leading-[1.2] tracking-wide"
						>
							"{currentTestimonial.quote}"
						</p>
					</blockquote>

					<!-- Attribution -->
					<div class="flex flex-col sm:flex-row items-center gap-4">
						{#if currentTestimonial.organizationLogoUrl}
							<div class="shrink-0 bg-white rounded-full p-3">
								<img
									src={currentTestimonial.organizationLogoUrl}
									alt={currentTestimonial.organizationName || 'Organization'}
									class="h-10 w-10 object-contain"
								/>
							</div>
						{:else if currentTestimonial.authorAvatarUrl}
							<div class="shrink-0">
								<img
									src={currentTestimonial.authorAvatarUrl}
									alt={currentTestimonial.authorName}
									class="h-16 w-16 rounded-full object-cover"
								/>
							</div>
						{/if}

						<div class="text-white">
							<p class="font-title text-lg md:text-3xl font-bold">
								{currentTestimonial.authorName}
							</p>
							<p class="text-base md:text-lg font-semibold">
								{currentTestimonial.authorRole}
								{#if currentTestimonial.organizationName}
									, {currentTestimonial.organizationName}
								{/if}
							</p>
						</div>
					</div>

					<!-- Carousel Navigation -->
					{#if data.testimonials.length > 1}
						<div class="flex items-center gap-6 mt-8">
							<!-- Previous Button -->
							<button
								onclick={() => previousTestimonial(data.testimonials.length)}
								class="text-white hover:text-gray-300 transition-colors"
								aria-label="Previous testimonial"
							>
								<ChevronLeft class="size-8" />
							</button>

							<!-- Dots Indicator -->
							<div class="flex gap-2">
								{#each data.testimonials as testimonial, index (testimonial.id)}
									<button
										onclick={() => goToTestimonial(index)}
										class={cn(
											'h-2 w-2 rounded-full transition-all',
											index === safeIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
										)}
										aria-label={`Go to testimonial ${index + 1}`}
									></button>
								{/each}
							</div>

							<!-- Next Button -->
							<button
								onclick={() => nextTestimonial(data.testimonials.length)}
								class="text-white hover:text-gray-300 transition-colors"
								aria-label="Next testimonial"
							>
								<ChevronRight class="size-8" />
							</button>
						</div>
					{/if}
				</div>
			</div>
		{/if}
	{:else}
		<!-- No Testimonials -->
		<div class="relative h-[400px] bg-muted flex items-center justify-center">
			<p class="text-muted-foreground">No testimonials available</p>
		</div>
	{/if}
</section>
